import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    suburb: "",
    inquiry: "General Question",
    message: "",
  });

  const inquiryOptions = [
    "Co-Hosting Inquiry",
    "Listing Audit",
    "Experience Booking",
    "General Question",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const subject = encodeURIComponent(
      `${formData.inquiry} from ${formData.name}`
    );
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone}\n` +
      `Property Suburb: ${formData.suburb}\n` +
      `Inquiry Type: ${formData.inquiry}\n\n` +
      `Message:\n${formData.message}`
    );
    
    window.location.href = `mailto:support@staycraftstudio.com?subject=${subject}&body=${body}`;
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section-padding bg-cream"
    >
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-4">
              Get in Touch
            </p>
            <h2 className="section-heading">
              Ready to earn more from your Airbnb?
            </h2>
            <p className="text-bark-light text-lg mt-4 max-w-2xl mx-auto">
              Book a free 30-minute consultation. No obligation, no hard sell —
              just honest advice about your listing.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gold-light text-gold flex items-center justify-center flex-shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-wider text-bark-light mb-1">
                      Email
                    </p>
                    <a
                      href="mailto:support@staycraftstudio.com"
                      className="text-bark hover:text-gold transition-colors"
                    >
                      support@staycraftstudio.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gold-light text-gold flex items-center justify-center flex-shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-wider text-bark-light mb-1">
                      Phone
                    </p>
                    <a
                      href="tel:+61400000000"
                      className="text-bark hover:text-gold transition-colors"
                    >
                      0400 000 000
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gold-light text-gold flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-wider text-bark-light mb-1">
                      Location
                    </p>
                    <p className="text-bark">
                      Based on Bribie Island, QLD
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form
                onSubmit={handleSubmit}
                className="bg-white p-8 border border-bark/10"
                data-testid="contact-form"
              >
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="form-label">
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Jane Smith"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="form-label">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="jane@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="phone" className="form-label">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="0400 000 000"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="suburb" className="form-label">
                      Property Suburb
                    </label>
                    <input
                      type="text"
                      id="suburb"
                      name="suburb"
                      value={formData.suburb}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="e.g. Woorim, Bongaree"
                      data-testid="contact-suburb-input"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="inquiry" className="form-label">
                    What do you need?
                  </label>
                  <select
                    id="inquiry"
                    name="inquiry"
                    value={formData.inquiry}
                    onChange={handleChange}
                    className="form-input"
                    data-testid="contact-inquiry-select"
                  >
                    {inquiryOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="form-label">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="form-input resize-none"
                    placeholder="Tell us about your property or what you're looking for..."
                    data-testid="contact-message-textarea"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-form-submit"
                >
                  Send My Inquiry
                  <Send size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
